import { absolutePositionToRelativePosition, initProseMirrorDoc, relativePositionToAbsolutePosition, ySyncPluginKey, } from 'y-prosemirror';
import * as Y from 'yjs';
const FRAGMENT_NAMES = ['default', 'prosemirror', 'content', ''];
export const findDefaultXmlFragment = (ydoc) => {
    for (const name of FRAGMENT_NAMES) {
        const fragment = ydoc.getXmlFragment(name);
        if (fragment.length > 0) {
            return fragment;
        }
    }
    return ydoc.getXmlFragment('default');
};
const getCollaborativeBinding = (editor) => {
    if (!editor || editor.isDestroyed) {
        return null;
    }
    try {
        const syncState = ySyncPluginKey.getState(editor.state);
        const binding = syncState?.binding;
        if (!binding || binding.isDestroyed || !binding.type || !binding.mapping) {
            return null;
        }
        return { fragment: binding.type, mapping: binding.mapping };
    }
    catch {
        return null;
    }
};
const getBindingFromYdoc = (ydoc, schema) => {
    const fragment = findDefaultXmlFragment(ydoc);
    if (!fragment) {
        return null;
    }
    try {
        const { mapping } = initProseMirrorDoc(fragment, schema);
        return { fragment, mapping };
    }
    catch {
        return null;
    }
};
const resolveBinding = (editor, ydoc) => {
    const collaborative = getCollaborativeBinding(editor);
    if (collaborative) {
        return collaborative;
    }
    if (!ydoc || ydoc.isDestroyed || !editor || editor.isDestroyed) {
        return null;
    }
    return getBindingFromYdoc(ydoc, editor.schema);
};
const encodeRelativePosition = (pos, binding) => {
    try {
        const relative = absolutePositionToRelativePosition(pos, binding.fragment, binding.mapping);
        return Y.relativePositionToJSON(relative);
    }
    catch {
        return null;
    }
};
const decodeRelativePosition = (ydoc, binding, relativeJson) => {
    if (!relativeJson) {
        return null;
    }
    try {
        const relative = Y.createRelativePositionFromJSON(relativeJson);
        return relativePositionToAbsolutePosition(ydoc, binding.fragment, relative, binding.mapping);
    }
    catch {
        return null;
    }
};
const encodeRange = (binding, from, to) => {
    const fromRel = encodeRelativePosition(from, binding);
    const toRel = from === to ? fromRel : encodeRelativePosition(to, binding);
    if (!fromRel || !toRel) {
        return null;
    }
    return { fromRel, toRel };
};
const resolveRange = (ydoc, binding, anchor, docSize) => {
    const from = decodeRelativePosition(ydoc, binding, anchor.fromRel);
    const to = decodeRelativePosition(ydoc, binding, anchor.toRel);
    if (from == null || to == null) {
        return null;
    }
    if (from < 0 || to < 0 || from > docSize || to > docSize) {
        return null;
    }
    return {
        from: Math.min(from, to),
        to: Math.max(from, to),
    };
};
export const encodeDocAnchorWithSchema = (ydoc, schema, from, to) => {
    if (!ydoc || ydoc.isDestroyed) {
        return null;
    }
    const binding = getBindingFromYdoc(ydoc, schema);
    if (!binding) {
        return null;
    }
    return encodeRange(binding, from, to);
};
export const resolveDocAnchorWithSchema = (ydoc, schema, anchor, docSize) => {
    if (!anchor?.fromRel || !anchor?.toRel || !ydoc || ydoc.isDestroyed) {
        return null;
    }
    const binding = getBindingFromYdoc(ydoc, schema);
    if (!binding) {
        return null;
    }
    return resolveRange(ydoc, binding, anchor, docSize);
};
export const encodeDocAnchor = (editor, ydoc, from, to) => {
    const binding = resolveBinding(editor, ydoc);
    if (!binding) {
        return null;
    }
    return encodeRange(binding, from, to);
};
export const resolveDocAnchor = (editor, ydoc, anchor) => {
    if (!anchor?.fromRel || !anchor?.toRel || !ydoc || ydoc.isDestroyed) {
        return null;
    }
    const binding = resolveBinding(editor, ydoc);
    if (!binding) {
        return null;
    }
    const docSize = editor?.state.doc.content.size ?? Number.POSITIVE_INFINITY;
    return resolveRange(ydoc, binding, anchor, docSize);
};
//# sourceMappingURL=doc-anchor.js.map